import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import { CustomerTable } from "../components/customers/CustomerTable";
import { Pagination } from "../components/common/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { CustomerForm } from "../components/customers/CustomerForm";
import { useCustomerManagement } from "../hooks/useCustomers";
import {
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
} from "../types/customer";
import PrimaryButton from "../components/ui/PrimaryButton";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ReactComponent as VerifyIcon } from "../assets/ic_check_verified.svg";
import { VerifyCustomerForm } from "../components/customers/VerifyCustomerForm";
import { useNavigate } from "react-router-dom";
import { useDailyEntryManagement } from "../hooks/useDailyEntries";

export const Customers: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { createCustomerEntry } = useDailyEntryManagement();
  const [showVerifyForm, setShowVerifyForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | undefined>(
    undefined,
  );
  const itemsPerPage = 25;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryParams = useMemo(
    () => ({
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      name: debouncedSearchTerm || undefined,
    }),
    [currentPage, debouncedSearchTerm],
  );

  const {
    customers,
    isLoading,
    isError,
    error,
    countries,
    isCountriesLoading,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    isCreating,
    isUpdating,
    isDeleting,
    createError,
    updateError,
    deleteError,
    refreshCustomers,
  } = useCustomerManagement(queryParams);

  const totalPages = useMemo(() => {
    if (!customers?.pagination?.total) return 1;
    return Math.ceil(customers.pagination.total / itemsPerPage);
  }, [customers?.pagination?.total]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleCreateCustomer = () => {
    setEditingCustomer(undefined);
    setShowForm(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const handleFormSubmit = (
    data: CreateCustomerRequest | UpdateCustomerRequest,
  ) => {
    if (editingCustomer) {
      updateCustomer(
        { customerId: editingCustomer.id, data: data as UpdateCustomerRequest },
        {
          onSuccess: () => {
            toast.success("Customer updated successfully!");
            setShowForm(false);
            setEditingCustomer(undefined);
          },
          onError: (error) => {
            toast.error(`Failed to update customer: ${error.message}`);
          },
        },
      );
    } else {
      createCustomer(data as CreateCustomerRequest, {
        onSuccess: () => {
          toast.success("Customer created successfully!");
          setShowForm(false);
        },
        onError: (error) => {
          toast.error(`Failed to create customer: ${error.message}`);
        },
      });
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<string | null>(null);

  const handleDeleteCustomer = (customerId: string) => {
    setCustomerToDelete(customerId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (customerToDelete) {
      deleteCustomer(customerToDelete, {
        onSuccess: () => {
          toast.success("Customer deleted successfully!");
          setDeleteDialogOpen(false);
          setCustomerToDelete(null);
        },
        onError: (error) => {
          toast.error(`Failed to delete customer: ${error.message}`);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCustomerToDelete(null);
  };

  const handleViewCustomer = (customer: Customer) => {
    navigate(`/customers/${customer.id}`);
  };

  const handleVerifyCustomerSuccess = () => {
    setShowVerifyForm(false);
  };

  const handleVerifyCancel = () => {
    setShowVerifyForm(false);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCustomer(undefined);
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>Error loading customers: {error?.message || "Unknown error"}</p>
          <button
            onClick={refreshCustomers}
            className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-3 md:p-6 relative">
      <div className="max-w-7xl mx-auto">
        <div className="bg-slate-800 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4 justify-between">
            {/* Search */}
            <div className="w-full lg:flex-1 lg:max-w-md">
              <SearchInput
                placeholder="Search customers..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
              <PrimaryButton
                onClick={() => setShowVerifyForm(true)}
                disabled={isCreating || isCountriesLoading}
                className="flex w-full sm:w-auto items-center justify-center gap-2 bg-slate-700 hover px-3 py-2 text-sm md:text-base"
              >
                <VerifyIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Verify Customer</span>
                <span className="sm:hidden">Verify</span>
              </PrimaryButton>

              <PrimaryButton
                onClick={handleCreateCustomer}
                disabled={isCreating || isCountriesLoading}
                className="flex w-full sm:w-auto items-center justify-center gap-2 px-3 py-2 text-sm md:text-base"
              >
                <Plus className="h-4 w-4" />
                {isCreating ? (
                  "Creating..."
                ) : (
                  <>
                    <span className="hidden sm:inline">Add Customer</span>
                    <span className="sm:hidden">Add</span>
                  </>
                )}
              </PrimaryButton>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {createError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Create Error: {createError.message}
          </div>
        )}
        {updateError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Update Error: {updateError.message}
          </div>
        )}
        {deleteError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Delete Error: {deleteError.message}
          </div>
        )}

        {/* Loading States */}
        {(isDeleting || isUpdating) && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            {isDeleting && "Deleting customer..."}
            {isUpdating && "Updating customer..."}
          </div>
        )}

        {/* Customer Table */}
        <CustomerTable
          customers={customers?.items || []}
          onEdit={handleEditCustomer}
          onDelete={handleDeleteCustomer}
          onView={handleViewCustomer}
          isLoading={isLoading}
        />

        {/* Pagination */}
        {customers?.items && customers.items.length > 0 && totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Stats */}
        {customers?.pagination && (
          <div className="mt-6 text-sm text-slate-400 text-center">
            Showing {customers.pagination.returned} of{" "}
            {customers.pagination.total} customers
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </div>
        )}
      </div>

      {/* Customer Form Modal */}
      {showForm && (
        <CustomerForm
          customer={editingCustomer}
          countries={countries}
          isLoading={isCreating || isUpdating || isCountriesLoading}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          onCustomerCreated={(newCustomer) => {
            createCustomerEntry(newCustomer.id, {
              onSuccess: () => {
                toast.success("Daily entry created for today!");
              },
              onError: (error) => {
                toast.error(`Failed to create daily entry: ${error.message}`);
              },
            });
          }}
        />
      )}

      {showVerifyForm && (
        <VerifyCustomerForm
          onCancel={handleVerifyCancel}
          onSuccess={handleVerifyCustomerSuccess}
          isLoading={false}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Customer"
        description="Are you sure you want to delete this customer? This action cannot be undone."
        icon={<Trash2 className="h-8 w-8 text-white" />}
        iconBgColor="bg-red-600"
        confirmColor="bg-red-600 hover:bg-red-700"
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* Blur overlay - shown when any modal is open */}
      {(showForm || showVerifyForm || deleteDialogOpen) && (
        <div className="fixed inset-0 bg-black/5 backdrop-blur-sm z-10" />
      )}
    </div>
  );
};
